import React, { useCallback, useEffect, useState } from 'react';
import withTranslation from 'hoc/withTranslation';
import styled from '@emotion/styled';
import { convertCostsFromTourPlanner } from 'utils/cost';
import { useDispatch, useSelector } from 'react-redux';
import Checkbox from 'components/Form/Checkbox';
import Spinner from 'components/Presentation/Spinner';
import { colors } from 'global/variables';
import download from 'global/img/download.svg';
import { css } from '@emotion/react';
import { saveToJsonFile } from 'utils/SolutionExport';
import {
  StyledFullWidthContainer,
  StyledIcon,
  StyledWizardContent,
  StyledWizardH1,
  StyledWizardH2,
} from '../Global/WizardStyled';
import Stops from './Stops';
import Label from '../../Form/Label';
import {
  getDemand,
  getLastValidIteration,
  getLoadPercentage,
  getVehicleTypeById,
  isDemandVisible,
  isRecharge,
} from '../../../utils/SolutionHelpers';
import TourDispatch from '../TourDispatch/TourDispatch';
import { convertToDisplayTime, getAsset, isMobileDevice } from '../../../utils/helpers';
import { getCSVConfigLastDemandLabel } from '../../../utils/csv/customConfig';
import { getSafeValue } from '../../../utils/security';
import { StyledTourDispatcherContainer } from '../Wizard';
import { getRoutingTourInfo, selectTourById } from '../../../actions';
import {
  tourToRoutingRequest,
  buildRouteSummaryFromSegments,
} from '../../../utils/RoutingConverter';
import TourDetails from './TourDetails';
import { StyledNotificationSpinner } from '../Global/Notifications';

const { aqua } = colors;

const StyledLabelContainer = styled.div({
  width: '50%',
});

const DownloadIconStyle = css`
  background-image: url(${getAsset(download)});
  width: 1.4rem !important;
  height: 1.4rem !important;
  background-size: 1.2rem !important;
  background-position-y: 3px;
  margin: 0 !important;
  padding-left: 2rem;
`;

const Step4Container = ({
  user,
  tourData: { fleet, plan, tours = [] },
  selectedTours,
  solution,
  tourPlanner,
  orders,
  helpers: { formatDuration },
  onSearchId,
  oAuth,
  translations,
}) => {
  if (tours.length === 0) {
    return <></>;
  }
  const dispatch = useDispatch();
  const routingTour = useSelector(({ routingTour: stateRoutingTour }) => stateRoutingTour);

  const handleSelectTourById = useCallback((data) => dispatch(selectTourById(data)), [dispatch]);
  const tempOrders = solution.requests[solution.show].tempOrders;
  const errorState = useSelector((state) => state.error);
  const hideRoutes = user.mapSettings.hideAllRoutes;
  const [enabledVRP, setEnableVRP] = useState(false);

  const handleGetRoutingTourInfo = useCallback(
    (index, tour, enblVRP) =>
      dispatch(
        getRoutingTourInfo({
          oAuth,
          routingRequest: tourToRoutingRequest(
            tour,
            tourPlanner,
            tempOrders || orders,
            enblVRP,
            hideRoutes,
          ),
          tourId: index,
          solutionId: solution.show,
          enblVRP,
        }),
      ),
    [dispatch, oAuth, tourPlanner, solution.show, orders, tempOrders, enabledVRP, hideRoutes],
  );

  const {
    display: { selectedTour },
  } = useSelector(({ mapContainer }) => mapContainer);
  const [demandLabel, setDemandLabel] = useState('');
  const [isLoading] = useState(false);

  const [isDemandVisibleLocal, setIsDemandVisibleLocal] = useState('');
  const {
    wizard: {
      tours: { vehicleLabel, runTimeTrans },
      toursDetails: { toursDetailsTitle, dropsTrans },
    },
  } = translations;

  useEffect(() => {
    const demandVisible = isDemandVisible(tempOrders || orders, tourPlanner);
    let label = '';
    if (demandVisible && !isMobileDevice()) {
      label = getCSVConfigLastDemandLabel(tourPlanner, 'demand', dropsTrans);
    }

    setIsDemandVisibleLocal(demandVisible);
    setDemandLabel(label);
  }, [orders, tourPlanner, dropsTrans, setDemandLabel, setIsDemandVisibleLocal, tempOrders]);

  const onChangeVrp = () => {
    setEnableVRP(!enabledVRP);
  };

  const tourId = selectedTour ?? 0;
  const tour = getSafeValue(tours, tourId);
  const sections = routingTour?.[tourId]?.sections;
  const walkingDistancesMap = routingTour?.[tourId]?.walkingDistancesMap;
  const isImperial = user.distance === 'imperial';
  const formattedDetails = convertCostsFromTourPlanner({
    distance: tour.statistic.distance,
    isImperial,
  });
  const runtime = solution.requests[solution.show].runtime;
  const formattedDuration = tour.statistic.duration
    ? formatDuration(Math.floor(tour.statistic.duration / 60))
    : 'Unknown';
  const vehicleType = getVehicleTypeById(tour.typeId, fleet);
  const co2Emission = tour.statistic?.co2Emission?.toFixed(2);
  const consumption = tour.statistic?.consumption?.toFixed(2);
  const totalDetails = {
    distance: formattedDetails.distance ? formattedDetails.distance.toFixed(2) : 0,
    time: formattedDuration,
    cost: tour.statistic.cost.toFixed(2),
    co2Emission: co2Emission || '',
    consumption: consumption || '',
  };
  const request = getLastValidIteration(solution.requests[solution.show]).request;
  const vehicleTerritories = vehicleType?.territories?.items.map((t) => t.id);
  const territories = vehicleTerritories?.length > 0 && vehicleTerritories.join(', ');
  const skills = vehicleType?.skills?.length > 0 && vehicleType.skills.join(', ');
  const tourDemand = getDemand({ tour, plan });
  const tourLoad = getLoadPercentage(tourDemand, vehicleType);
  const changeUiPosition = tourLoad && isDemandVisibleLocal;

  const rechargeStops = tour.stops.filter((stop) =>
    stop.activities?.some((a) => isRecharge(a.type)),
  );
  const rechargeInfo = (() => {
    if (rechargeStops.length === 0) return null;
    const visitCounts = rechargeStops.reduce((acc, stop) => {
      const act = stop.activities.find((a) => isRecharge(a.type));
      const name = act?.jobTag || act?.jobId || null;
      if (name) acc[name] = (acc[name] || 0) + 1;
      return acc;
    }, {});
    const stationLines = Object.entries(visitCounts).map(
      ([name, visits]) => `${name} (x${visits})`,
    );
    return { count: rechargeStops.length, stationLines };
  })();
  const smallerUi =
    territories ||
    skills ||
    (changeUiPosition && (totalDetails.consumption || totalDetails.co2Emission || rechargeInfo));

  useEffect(() => {
    handleSelectTourById({ selectedTour: tourId });
    handleGetRoutingTourInfo(tourId, tour);
  }, [tour, oAuth]);

  useEffect(() => {
    handleGetRoutingTourInfo(tourId, tour, enabledVRP);
  }, [tour, enabledVRP]);

  const onDownloadClick = () => {
    const fileName = 'routing_info.json';
    saveToJsonFile(sections, fileName);
  };

  const stopsToCompare = buildRouteSummaryFromSegments(sections);

  useEffect(() => {
    if (errorState) {
      setEnableVRP(false);
    }
  }, [errorState]);

  return (
    <StyledWizardContent data-test-id="tourDetailsPanel">
      {isLoading ? (
        <StyledNotificationSpinner absolute>
          <Spinner color={aqua} />
        </StyledNotificationSpinner>
      ) : (
        <Checkbox
          id="objective-functions-enabler"
          value={enabledVRP ?? false}
          contrast
          onChange={onChangeVrp}
          absolute
          title={enabledVRP ? translations.global.disableVRP : translations.global.enableVRP}
        />
      )}
      <StyledWizardH1 noMargin marginTop>
        {toursDetailsTitle}
      </StyledWizardH1>
      <StyledWizardH2 noMargin marginTop>
        {`${runTimeTrans}: ${convertToDisplayTime(runtime)}`}
        {enabledVRP && (
          <StyledIcon
            icon={DownloadIconStyle}
            onClick={onDownloadClick}
            title={translations.global.downloadRoutingInfo}
          />
        )}
      </StyledWizardH2>
      <StyledLabelContainer>
        <Label title={tour.vehicleId} lowerCase>{`${vehicleLabel}: ${tour.vehicleId}`}</Label>
      </StyledLabelContainer>
      {tour && request && (
        <StyledTourDispatcherContainer step4>
          <TourDispatch
            user={user}
            selectedTours={selectedTours}
            solution={solution}
            tourPlanner={tourPlanner}
            tour={tour}
            request={request}
            origin="tour details"
            openIdSearch={() => onSearchId()}
            fleet={fleet}
            oAuth={oAuth}
            orders={orders}
          />
        </StyledTourDispatcherContainer>
      )}
      <TourDetails
        user={user}
        tours={tours}
        tourPlanner={tourPlanner}
        demandLabel={demandLabel}
        isDemandVisibleLocal={isDemandVisibleLocal}
        totalDetails={totalDetails}
        territories={territories}
        skills={skills}
        vehicleType={vehicleType}
        tourLoad={tourLoad}
        tourDemand={tourDemand}
        rechargeInfo={rechargeInfo}
      />
      <StyledFullWidthContainer>
        <Stops
          stops={tour.stops}
          stopsCompare={enabledVRP ? stopsToCompare : null}
          request={request}
          tourPlanner={tourPlanner}
          enableVRP={enabledVRP}
          user={user}
          demandLabel={demandLabel}
          isDemandVisible={isDemandVisibleLocal}
          tourId={tourId}
          typeId={tour.typeId}
          smaller={smallerUi}
          walkingDistancesMap={walkingDistancesMap}
        />
      </StyledFullWidthContainer>
    </StyledWizardContent>
  );
};

export default withTranslation(Step4Container);
